/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import './styles.scss';
import type { JSX } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { $getRoot, EditorState } from 'lexical';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { UNORDERED_LIST, ORDERED_LIST } from '@lexical/markdown';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { AutoLinkPlugin, createLinkMatcherWithRegExp } from '@lexical/react/LexicalAutoLinkPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';

import { OnChangePlugin } from '@features/rich-text-editor/plugins/OnChangePlugin';
import { ToolbarPlugin } from '@features/rich-text-editor/plugins/ToolbarPlugin';
import { defaultConfig } from '@features/rich-text-editor/constants';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { EditorPlaceholder } from '@features/rich-text-editor/EditorPlaceholder';
import { VariablePlugin } from '@features/rich-text-editor/plugins/VariablePlugin';
import { VariableTypeaheadPlugin } from '@features/rich-text-editor/plugins/VariablePlugin/VariableTypeaheadPlugin';
import { VariableFormatPlugin } from '@features/rich-text-editor/plugins/VariablePlugin/VariableFormatPlugin';
import { VariableBackspacePlugin } from '@features/rich-text-editor/plugins/VariablePlugin/VariableBackspacePlugin';
import '@features/rich-text-editor/plugins/VariablePlugin/VariablePlugin.scss';

const initialConfig = {
  ...defaultConfig,
  namespace: 'RichTextEditor',
};

const URL_REGEX =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)(?<![-.+():%])/;

const EMAIL_REGEX =
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;

/**
 * Reference: https://github.com/facebook/lexical/blob/main/packages/lexical-playground/src/plugins/AutoLinkPlugin/index.tsx
 */
const MATCHERS = [
  createLinkMatcherWithRegExp(URL_REGEX, (text) => (text.startsWith('http') ? text : `https://${text}`)),
  createLinkMatcherWithRegExp(EMAIL_REGEX, (text) => `mailto:${text}`),
];

export const RichTextEditorContainer = ({ children }: { children: JSX.Element }) => {
  return <LexicalComposer initialConfig={initialConfig}>{children}</LexicalComposer>;
};

interface Props {
  placeholder?: string;
  disabled?: boolean;
  handleChange: ({ editorState, plainText }: { editorState: EditorState; plainText: string }) => void;
  autoFocus?: boolean;
  enableVariables?: boolean;
}

export const RichTextEditor = ({ disabled, placeholder, handleChange, autoFocus, enableVariables }: Props) => {
  const getPlainTextBeforeHandleChange = (editorState: EditorState) => {
    const plainText = editorState.read(() => $getRoot().getTextContent());
    handleChange({ editorState, plainText });
  };

  return (
    <div className='editor-container position-relative'>
      {disabled ? null : <ToolbarPlugin enableVariables={enableVariables} />}
      <div className='position-relative'>
        {autoFocus ? <AutoFocusPlugin /> : null}
        <RichTextPlugin
          contentEditable={<ContentEditable className='editor-input position-relative px-2 pt-1 pb-3' />}
          placeholder={<EditorPlaceholder placeholder={placeholder} />}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <ListPlugin />
        <HorizontalRulePlugin />
        <HistoryPlugin />
        <OnChangePlugin onChange={getPlainTextBeforeHandleChange} />
        <MarkdownShortcutPlugin transformers={[UNORDERED_LIST, ORDERED_LIST]} />
        <LinkPlugin validateUrl={(url) => /^https?:\/\/|^mailto:/.test(url)} />
        <AutoLinkPlugin matchers={MATCHERS} />
        {enableVariables ? (
          <>
            <VariablePlugin />
            <VariableTypeaheadPlugin />
            <VariableFormatPlugin />
            <VariableBackspacePlugin />
          </>
        ) : null}
      </div>
    </div>
  );
};
